import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useParams } from "react-router-dom";
import styles from "./record.module.css";
import { MyIcons } from "../../components/icons/my-icons";
import { getRecordById, getRecordStatistics } from "../../services/apiService";
import {
  ExpedienteLockProvider,
  useExpedienteLock,
} from "../../contexts/ExpedienteLockContext";
import ExpedienteLockModal from "../../components/modals/expediente-lock-modal";
import ExpedienteLockBanner from "../../components/screens/ExpedienteLockBanner";
import usePageTitle from "../../hooks/usePageTitle";

// Componente interno que usa el contexto de bloqueo
const RecordPageContent = ({ id, recordData, recordStatistics, refetchRecord }) => {
  usePageTitle(`Expediente ${id}`);
  const {
    lockInfo,
    takeoverRequest,
    pendingTakeoverRequest,
    requestTakeoverControl,
    respondToTakeoverRequest,
    dismissTakeoverRequest,
    isFirstUser,
  } = useExpedienteLock();

  const [showLockModal, setShowLockModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);

  const isLockedByOther = lockInfo.isLocked && !lockInfo.lockedByMe;

  // Mostrar modal cuando hay una solicitud de takeover
  useEffect(() => {
    if (takeoverRequest) {
      setShowLockModal(true);
    }
  }, [takeoverRequest]);

  // Mostrar modal automático cuando el expediente está bloqueado por otro
  useEffect(() => {
    if (isLockedByOther && !modalDismissed && !isFirstUser) {
      setShowLockModal(true);
    } else if (!isLockedByOther) {
      setModalDismissed(false);
    }
  }, [isLockedByOther, modalDismissed, isFirstUser]);

  const handleCloseModal = () => {
    setShowLockModal(false);
    setModalDismissed(true);
  };

  const handleTakeoverRequest = () => {
    requestTakeoverControl();
    setShowLockModal(false);
  };

  const handleTakeoverResponse = (approved) => {
    respondToTakeoverRequest(approved);
  };

  return (
    <>
      {/* Modal de bloqueo */}
      <ExpedienteLockModal
        isOpen={showLockModal}
        onDismiss={handleCloseModal}
        lockInfo={lockInfo}
        takeoverRequest={takeoverRequest}
        pendingTakeoverRequest={pendingTakeoverRequest}
        onRequestTakeover={handleTakeoverRequest}
        onRespondTakeover={handleTakeoverResponse}
      />

      <div className={styles.headerContainer}>
        <div className={styles.pageTitleContainer}>
          <MyIcons
            height={32}
            width={32}
            name={"fileFilled"}
            color={"var(--black)"}
          />
          <div style={{flexDirection: 'row', display: 'flex', alignItems: 'center', gap: '16px', }}>
            <h1 className={styles.title} >Expediente {id}</h1>
            <div style={{flexDirection: 'row', display: 'flex', alignItems: 'center', gap: '16px', alignSelf: 'center', border: '1px solid var(--light-grey)', padding: '4px 8px', borderRadius: '8px' }}>
            <span className={styles.infoText}>
              Coste:{" "}
              {recordStatistics?.costeTotal?.toLocaleString("es-ES", {
                style: "currency",
                currency: "EUR",
              }) || "0€"}
            </span>
            <div>
              <span className={styles.infoText}>Facturado:</span>
              <span
                className={styles.infoText}
                style={{ color: "var(--green)" }}
              >
                {" " +
                  (recordStatistics?.facturado?.toLocaleString("es-ES", {
                    style: "currency",
                    currency: "EUR",
                  }) || "0€")}
              </span>
            </div>
            <span className={styles.infoText}>
              R.Conjunto:{" "}
              {recordStatistics?.conjunto
                ? recordStatistics.conjunto.toFixed(2)
                : "0"}
            </span>
            <span className={styles.infoText}>
              % Beneficio:{" "}
              {recordStatistics?.beneficio
                ? recordStatistics.beneficio.toFixed(2) + "%"
                : "0%"}
            </span>
            </div>
          </div>
        </div>
      </div>

      <nav className={styles.navbar}>
        <NavLink
          to="information"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Información
        </NavLink>
        <NavLink
          to="appointments"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Citas
        </NavLink>
        <NavLink
          to="budget-lines"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Líneas de Presupuesto
        </NavLink>
        <NavLink
          to="budgets"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Presupuestos
        </NavLink>

        <NavLink
          to="invoices"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Facturas
        </NavLink>

        <NavLink
          to="notes"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Anotaciones
        </NavLink>

        <NavLink
          to="files"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Archivos
        </NavLink>
        <NavLink
          to="reminders"
          className={({ isActive }) => (isActive ? `${styles.active}` : "")}
        >
          Recordatorios
        </NavLink>
      </nav>

      {/* Banner de bloqueo - visible cuando hay información de lock relevante */}
      {lockInfo.isLocked && lockInfo && (
        <ExpedienteLockBanner
          lockInfo={lockInfo}
          pendingTakeoverRequest={pendingTakeoverRequest}
          onRequestControl={handleTakeoverRequest}
          isConnected={true}
          isFirstUser={isFirstUser}
        />
      )}

      <Outlet context={{ recordData, lockInfo, isLockedByOther, refetchRecord, refetchStatistics: refetchRecord }} />
    </>
  );
};

const RecordPage = () => {
  const { id } = useParams();
  const [recordData, setRecordData] = useState(null);
  const [recordStatistics, setRecordStatistics] = useState(null);

  // Obtener el userId del localStorage
  const userId = localStorage.getItem("usuarioId");

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const data = await getRecordById(id);
        console.log("Record data:", data);
        setRecordData(data);

        const statistics = await getRecordStatistics(id);
        console.log("Record statistics:", statistics);
        setRecordStatistics(statistics);
      } catch (error) {
        console.error("Error fetching record data:", error);
      }
    };

    fetchRecord();
  }, [id]);

  // Función para permitir que los hijos forcen un re-fetch del expediente
  const refetchRecord = useCallback(async () => {
    try {
      const data = await getRecordById(id);
      setRecordData(data);
      const statistics = await getRecordStatistics(id);
      setRecordStatistics(statistics);
    } catch (error) {
      console.error('Error refetching record:', error);
    }
  }, [id]);

  // Escuchar evento global de fallback para forzar refetch desde modales que no pasen onSuccess
  useEffect(() => {
    const handler = () => {
      try {
        refetchRecord();
      } catch (e) {
        console.error('Error handling AGER:REFETCH_RECORD event', e);
      }
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('AGER:REFETCH_RECORD', handler);
    }

    return () => {
      if (typeof window !== 'undefined' && window.removeEventListener) {
        window.removeEventListener('AGER:REFETCH_RECORD', handler);
      }
    };
  }, [refetchRecord]);

  return (
    <div>
      {/* Proporcionar contexto de bloqueo de expediente */}
      <ExpedienteLockProvider expedienteId={id} userId={userId}>
        <RecordPageContent
          id={id}
          recordData={recordData}
          recordStatistics={recordStatistics}
          refetchRecord={refetchRecord}
        />
      </ExpedienteLockProvider>
    </div>
  );
};

export default RecordPage;
